import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const NONE = "__none__";
const empty = { kennzeichen: "", fahrzeug: "", hersteller: "", modell: "", fahrer: "", team: "", is_active: true, bemerkung: "" };

export default function Vehicles() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [v, u] = await Promise.all([api.get("/vehicles"), api.get("/users")]);
    setItems(v.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [v.kennzeichen, v.fahrzeug, v.hersteller, v.modell, v.fahrer, v.team, v.bemerkung].filter(Boolean).join(" ").toLowerCase().includes(s);
  });

  const startEdit = (v) => { setEditingId(v.id); setForm({ ...empty, ...v }); setOpen(true); };
  const startNew = () => { setEditingId(null); setForm(empty); setOpen(true); };

  const save = async () => {
    if (!form.kennzeichen) { toast.error("Kennzeichen erforderlich"); return; }
    try {
      const payload = { ...form, fahrer: form.fahrer === NONE ? "" : form.fahrer };
      if (editingId) await api.put(`/vehicles/${editingId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Gespeichert"); setOpen(false); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (v) => {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    try { await api.delete(`/vehicles/${v.id}`); await load(); toast.success("Gelöscht"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const activeUsers = users.filter((u) => u.is_active);

  return (
    <div data-testid="vehicles-page">
      <PageHeader
        title="Fahrzeuge"
        subtitle={`${items.length} Fahrzeuge im System`}
        actions={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={startNew} data-testid="vehicle-new-btn">
                <Plus className="h-4 w-4 mr-2" /> Neues Fahrzeug
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{editingId ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kennzeichen *</Label><Input className="h-11 mt-1" value={form.kennzeichen} onChange={(e) => setForm({ ...form, kennzeichen: e.target.value })} data-testid="v-kennzeichen" /></div>
                <div><Label>Fahrzeugname</Label><Input className="h-11 mt-1" value={form.fahrzeug} onChange={(e) => setForm({ ...form, fahrzeug: e.target.value })} data-testid="v-fahrzeug" /></div>
                <div><Label>Hersteller</Label><Input className="h-11 mt-1" value={form.hersteller || ""} onChange={(e) => setForm({ ...form, hersteller: e.target.value })} data-testid="v-hersteller" /></div>
                <div><Label>Modell</Label><Input className="h-11 mt-1" value={form.modell || ""} onChange={(e) => setForm({ ...form, modell: e.target.value })} data-testid="v-modell" /></div>
                <div>
                  <Label>Fahrer</Label>
                  <Select value={form.fahrer || NONE} onValueChange={(v) => setForm({ ...form, fahrer: v === NONE ? "" : v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="v-fahrer"><SelectValue placeholder="— wählen —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— keiner —</SelectItem>
                      {activeUsers.map((u) => (<SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>))}
                      {/* Show stored value even if user inactive */}
                      {form.fahrer && !activeUsers.find((u) => u.name === form.fahrer) && (
                        <SelectItem value={form.fahrer}>{form.fahrer} (inaktiv)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Team</Label><Input className="h-11 mt-1" value={form.team || ""} onChange={(e) => setForm({ ...form, team: e.target.value })} data-testid="v-team" /></div>
                <div className="col-span-2 flex items-center justify-between bg-slate-50 p-3 rounded">
                  <Label>Aktiv</Label>
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                </div>
                <div className="col-span-2"><Label>Bemerkung</Label><Textarea className="mt-1 min-h-[60px]" value={form.bemerkung || ""} onChange={(e) => setForm({ ...form, bemerkung: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={save} className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white" data-testid="v-save">Speichern</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="relative max-w-md mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="h-11 pl-10" placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicle-search" />
      </div>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Kennzeichen</th>
              <th className="text-left px-4 py-3">Fahrzeug</th>
              <th className="text-left px-4 py-3">Hersteller</th>
              <th className="text-left px-4 py-3">Modell</th>
              <th className="text-left px-4 py-3">Fahrer</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Keine Fahrzeuge</td></tr>}
            {filtered.map((v) => (
              <tr key={v.id} className="border-t" style={{ borderColor: "#E2E8F0" }}>
                <td className="px-4 py-2.5 font-semibold mono">{v.kennzeichen}</td>
                <td className="px-4 py-2.5">{v.fahrzeug}</td>
                <td className="px-4 py-2.5">{v.hersteller}</td>
                <td className="px-4 py-2.5">{v.modell}</td>
                <td className="px-4 py-2.5">{v.fahrer}</td>
                <td className="px-4 py-2.5">{v.team}</td>
                <td className="px-4 py-2.5"><span className={`text-xs font-semibold ${v.is_active ? "text-emerald-700" : "text-slate-400"}`}>{v.is_active ? "Aktiv" : "Inaktiv"}</span></td>
                <td className="px-4 py-2.5 flex justify-end gap-2">
                  {isAdmin && (
                    <>
                      <button className="p-1.5 hover:text-[#0A2540]" onClick={() => startEdit(v)} data-testid={`v-edit-${v.kennzeichen}`}><Pencil className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:text-red-600" onClick={() => del(v)} data-testid={`v-del-${v.kennzeichen}`}><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ActionBar onPrint={() => window.print()} hideNext />
    </div>
  );
}
